import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ExternalLink,
  Image as ImageIcon,
  X,
} from 'lucide-react';
import { Button } from '@/ui';

interface ImageData {
  id: string;
  url: string;
  alt: string;
  sourceUrl: string; // URL de l'article d'origine
  title?: string;
  description?: string;
}

interface ImageSliderProps {
  images: ImageData[];
  className?: string;
}

interface ImageZoomModalProps {
  image: ImageData;
  isOpen: boolean;
  onClose: () => void;
}

const ImageZoomModal: React.FC<ImageZoomModalProps> = ({
  image,
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
      <div className="relative max-w-4xl max-h-4xl w-full h-full flex items-center justify-center p-2 sm:p-4">
        {/* Bouton fermer */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white hover:text-gray-300 p-1 sm:p-2 rounded-full hover:bg-black hover:bg-opacity-50 transition-all z-10"
        >
          <X className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>

        {/* Image */}
        <img
          src={image.url}
          alt={image.alt}
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
        />

        {/* Informations */}
        <div className="absolute bottom-2 left-2 right-2 sm:bottom-4 sm:left-4 sm:right-4 bg-black bg-opacity-70 text-white p-3 sm:p-4 rounded-lg max-w-[90vw]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex-1 min-w-0">
              {image.title && (
                <h3 className="font-medium text-base sm:text-lg truncate">
                  {image.title}
                </h3>
              )}
              {image.description && (
                <p className="text-xs sm:text-sm text-gray-300 mt-1 line-clamp-2">
                  {image.description}
                </p>
              )}
            </div>
            <a
              href={image.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-lg text-xs sm:text-sm transition-colors flex-shrink-0"
            >
              <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Voir l'article</span>
              <span className="xs:hidden">Article</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ImageSlider: React.FC<ImageSliderProps> = ({
  images,
  className = '',
}) => {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<ImageData | null>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [imageWidth, setImageWidth] = useState(112); // Largeur par défaut
  const sliderRef = useRef<HTMLDivElement>(null);

  // Calculer la taille optimale des images selon l'espace disponible
  useEffect(() => {
    const calculateImageSize = () => {
      if (sliderRef.current && images.length > 0) {
        const containerWidth =
          sliderRef.current.parentElement?.clientWidth || window.innerWidth;
        const padding = window.innerWidth < 640 ? 16 : 32; // p-2 = 16px, p-4 = 32px
        const availableWidth = containerWidth - padding;

        // Calculer combien d'images peuvent tenir avec un gap minimum
        const minGap = 4;
        const maxImagesVisible = Math.min(
          images.length,
          window.innerWidth < 640 ? 3 : 4
        );
        const totalGaps = (maxImagesVisible - 1) * minGap;
        const maxImageWidth = (availableWidth - totalGaps) / maxImagesVisible;

        // Limites min/max pour la largeur des images
        const minWidth = 80;
        const maxWidth = window.innerWidth < 640 ? 140 : 280;

        const optimalWidth = Math.max(
          minWidth,
          Math.min(maxWidth, maxImageWidth)
        );
        setImageWidth(Math.floor(optimalWidth));
      }
    };

    calculateImageSize();
    window.addEventListener('resize', calculateImageSize);

    return () => window.removeEventListener('resize', calculateImageSize);
  }, [images.length]);

  if (images.length === 0) return null;

  const scroll = (direction: 'left' | 'right') => {
    if (sliderRef.current) {
      // Calculer dynamiquement la largeur de scroll basée sur la taille de l'écran
      const width = window.innerWidth;
      let scrollAmount;

      // Calculer le scroll basé sur la largeur dynamique des images
      const gap = width < 640 ? 4 : 8;
      scrollAmount = imageWidth + gap;

      const newPosition =
        direction === 'left'
          ? Math.max(0, scrollPosition - scrollAmount)
          : scrollPosition + scrollAmount;

      sliderRef.current.scrollTo({ left: newPosition, behavior: 'smooth' });
      setScrollPosition(newPosition);
    }
  };

  const handleImageClick = (image: ImageData) => {
    setSelectedImage(image);
  };

  return (
    <>
      <div
        className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2 sm:p-4 max-w-[90vw] ${className}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <ImageIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
            <h3 className="font-medium dark:text-gray-100 text-gray-900 text-sm sm:text-base">
              {t('images.title', 'Images des articles')} ({images.length})
            </h3>
          </div>
        </div>

        {/* Slider */}
        <div className="relative">
          {/* Boutons de navigation - Masqués sur mobile pour éviter le débordement */}
          {images.length > 3 && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="absolute -left-2 top-1/2 transform -translate-y-1/2 z-10 bg-white shadow-lg rounded-full w-8 h-8 p-0 hidden md:flex items-center justify-center"
                onClick={() => scroll('left')}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="absolute -right-2 top-1/2 transform -translate-y-1/2 z-10 bg-white shadow-lg rounded-full w-8 h-8 p-0 hidden md:flex items-center justify-center"
                onClick={() => scroll('right')}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </>
          )}

          {/* Images */}
          <div
            ref={sliderRef}
            className="flex overflow-x-auto scrollbar-hide py-2 px-0"
            style={{
              gap: `${window.innerWidth < 640 ? 4 : 8}px`,
              scrollBehavior: 'smooth',
            }}
          >
            {images.map((image) => (
              <div
                key={image.id}
                className="flex-shrink-0 relative group cursor-pointer"
                onClick={() => handleImageClick(image)}
              >
                <div
                  className="bg-gray-100 rounded-lg overflow-hidden border hover:border-blue-300 transition-all"
                  style={{
                    width: `${imageWidth}px`,
                    height: `${Math.floor(imageWidth * 0.6)}px`, // Ratio 5:3
                  }}
                >
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    onError={(e) => {
                      // Fallback en cas d'erreur de chargement
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div class="w-full h-full flex items-center justify-center bg-gray-100">
                            <div class="text-center text-gray-500">
                              <svg class="w-8 h-8 mx-auto mb-2 opacity-50" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                              </svg>
                              <p class="text-xs">Image non disponible</p>
                            </div>
                          </div>
                        `;
                      }
                    }}
                  />
                </div>

                {/* Overlay avec zoom */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="bg-white rounded-full p-2 shadow-lg">
                      <ZoomIn className="w-5 h-5 text-gray-700" />
                    </div>
                  </div>
                </div>

                {/* Titre de l'image */}
                {image.title && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 sm:p-3 rounded-b-lg">
                    <p className="text-white text-xs sm:text-sm font-medium truncate">
                      {image.title}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal de zoom */}
      <ImageZoomModal
        image={selectedImage!}
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
      />
    </>
  );
};

export default ImageSlider;
